'use client';

import React, { useState, useEffect, useCallback, useRef } from 'react';
import { DataNode, FilterState, Topic, TransactionApiResponse, type Transaction } from '@/types';
import { TransactionTable } from '@/components/TransactionTable';
import { X } from 'lucide-react';
import { apiHelper } from '@/helpers/apiHelper';
import GradientText from '@/components/GradiantTitle';

interface filterData {
    Tag: string;
    Label: string;
    Values: { Code: string, Label: string }[];
}
interface TransactionProps {
    row: DataNode;
    onClose: () => void;
    // User selection values from dashboard
    selectedTenant: string;
    selectedAreas: string[];
    selectedOutlets: string[];
    selectedBusinessDay: string;
    // Full dashboard data to find parent relationships
    dashboardData: DataNode[];
    // Tenants data to get tenant code
    tenantsData: {
        Tenants: Array<{
            TenantId: number;
            TenantCode: string;
            TenantName: string;
            Areas: Array<{
                AreaId: number;
                AreaCode: string;
                AreaName: string;
                Outlets: Array<{
                    OutletId: number;
                    OutletCode: string;
                    OutletName: string;
                }>;
            }>;
        }>
    };
    // Topics data to get Topic.Tag from Topic.Label
    topicsData: {
        Topics: Topic[];
    };
    filterState: FilterState;
    filtersData: {
        Filters: filterData[];
    };
}

export function Transaction({
    row,
    onClose,
    selectedTenant,
    selectedAreas,
    selectedOutlets,
    selectedBusinessDay,
    dashboardData,
    tenantsData,
    topicsData,
    filterState,
    filtersData
}: TransactionProps) {
    const [isVisible, setIsVisible] = useState(false);
    const [isClosing, setIsClosing] = useState(false);
    const [apiResponse, setApiResponse] = useState<TransactionApiResponse | null>(null);
    const [loading, setLoading] = useState(false);
    const [loadingMore, setLoadingMore] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [currentPage, setCurrentPage] = useState(0);
    const [hasMoreData, setHasMoreData] = useState(true);
    const isLoadingMoreRef = useRef(false);
    const [transactionDetailsFilters, setTransactionDetailsFilters] = useState<{ filters: Array<{ Tag: string, Values: string[] }>, isTopic: boolean }>({ filters: [], isTopic: false });

    // Function to get display text for filter values
    const getFilterDisplayText = useCallback((filterTag: string, values: string[]): string => {
        const filter = filtersData.Filters.find(f => f.Tag === filterTag);
        if (!filter || !filter.Values) return values.join(', ');
        
        const selectedLabels = values.map(value => {
            const option = filter.Values?.find(opt => opt.Code === value);
            return option ? option.Label : value;
        });


        const totalValues = filter.Values.length;
        const isAllSelected = selectedLabels.length === totalValues;
        if (isAllSelected) {
            return 'All';
        } else if (selectedLabels.length === 1) {
            return selectedLabels[0];
        } else {
            return `${selectedLabels.length} selected`;
        }
    }, [filtersData]);

    const handleClose = useCallback(() => {
        setIsClosing(true);
        // Wait for animation to complete before calling onClose
        setTimeout(() => {
            onClose();
        }, 300);
    }, [onClose]);

    useEffect(() => {
        // Trigger opening animation
        const timer = setTimeout(() => setIsVisible(true), 10);
        return () => clearTimeout(timer);
    }, []);

    // Prevent body scrolling when modal is open
    useEffect(() => {
        // Store the original overflow style
        const originalOverflow = document.body.style.overflow;

        // Disable body scrolling
        document.body.style.overflow = 'hidden';

        // Cleanup: restore original overflow when component unmounts
        return () => {
            document.body.style.overflow = originalOverflow;
        };
    }, []);



    // Function to find parent node in the hierarchy
    const findParentNode = useCallback((targetNode: DataNode, data: DataNode[]): DataNode | null => {
        for (const node of data) {
            if (node.ChildNodes) {
                for (const child of node.ChildNodes) {
                    if (child.Id === targetNode.Id) {
                        return node;
                    }
                    // Recursively search in deeper levels
                    const found = findParentNode(targetNode, [child]);
                    if (found) return found;
                }
            }
        }
        return null;
    }, []);

    // Function to build filters based on clicked node hierarchy
    const buildFiltersFromNodeHierarchy = useCallback((clickedNode: DataNode, dashboardData: DataNode[]): { filters: Array<{ Tag: string, Values: string[] }>, isTopic: boolean } => {
        const filters: Array<{ Tag: string, Values: string[] }> = [];
        const usedFilterTags = new Set<string>();
        // If user clicked on TOPIC: apply all selected filters as-is
        if (clickedNode.NodeTag === 'TOPIC') {
            // Include AREA and OUTLET selections if any
            if (selectedAreas.length > 0) {
                filters.push({ Tag: 'AREA', Values: selectedAreas });
                usedFilterTags.add('AREA');
            }
            if (selectedOutlets.length > 0) {
                filters.push({ Tag: 'OUTLET', Values: selectedOutlets });
                usedFilterTags.add('OUTLET');
            }

            // Include all user-selected filters from filterState
            Object.keys(filterState).forEach(filterTag => {
                const values = filterState[filterTag] || [];
                if (values.length > 0) {
                    filters.push({ Tag: filterTag, Values: values });
                    usedFilterTags.add(filterTag);
                }
            });

            return { filters: filters, isTopic: true };
        }


        // Otherwise: start with the clicked node itself
        filters.push({ Tag: clickedNode.NodeTag, Values: [clickedNode.NodeValue] });
        usedFilterTags.add(clickedNode.NodeTag);

        // Traverse upwards to find all parent nodes (excluding the root topic node)
        let currentNode = clickedNode;
        let parentNode = findParentNode(currentNode, dashboardData);

        while (parentNode && parentNode.NodeTag !== 'TOPIC') {
            if (!usedFilterTags.has(parentNode.NodeTag)) {
                filters.push({ Tag: parentNode.NodeTag, Values: [parentNode.NodeValue] });
                usedFilterTags.add(parentNode.NodeTag);
            }
            currentNode = parentNode;
            parentNode = findParentNode(currentNode, dashboardData);
        }

        // Include all user-selected filters from filterState except the already used filter above
        Object.keys(filterState).filter(filterTag => !usedFilterTags.has(filterTag)).forEach(filterTag => {
            const values = filterState[filterTag] || [];
            if (values.length > 0) {
                filters.push({ Tag: filterTag, Values: values });
                usedFilterTags.add(filterTag);
            }
        });


        return { filters: filters, isTopic: false };
    }, [findParentNode, filterState, selectedAreas, selectedOutlets]);

    // Function to get parent topic from the row hierarchy
    const getParentTopic = useCallback((currentRow: DataNode, data: DataNode[]): string => {
        let currentNode = currentRow;

        while (currentNode) {
            if (currentNode.NodeTag === 'TOPIC') {
                return currentNode.NodeValue;
            }

            // Find parent node
            const parent = findParentNode(currentNode, data);

            if (!parent) {
                const topic = topicsData.Topics.find(t => t.Label === currentNode.NodeLabel);
                return topic?.Tag || currentNode.NodeTag;
            } else {
                currentNode = parent;
            }
        }

        return '';
    }, [findParentNode, topicsData.Topics]);

    // Function to call the API with specific page
    const callGetTransactionsAPI = useCallback(async (pageIndex: number, isLoadMore: boolean = false) => {
        if (isLoadMore) {
            setLoadingMore(true);
        } else {
            setLoading(true);
        }
        setError(null);

        try {
            // Build filters using hierarchy + remaining selections
            const filters = buildFiltersFromNodeHierarchy(row, dashboardData);
            setTransactionDetailsFilters({ filters: filters.filters, isTopic: filters.isTopic });
            const requestPayload = {
                BusinessDay: selectedBusinessDay,
                Topic: getParentTopic(row, dashboardData),
                Filters: filters.filters,
                Pagination: {
                    PageIndex: pageIndex,
                    PageSize: 250 // Using 250 as per the curl example
                }
            };

            const data = await apiHelper({
                externalurl: '/api/v1/Reconciliation/GetReconciliationConsoleTransactions',
                activityTag: 'GetReconciliationConsoleTransactions',
                onlyBody: requestPayload,
                method: 'POST',
                headerContentType: 'application/json'
            });

            if (isLoadMore) {
                // Append new transactions to existing ones, filtering out duplicates
                if (!data.Transactions) {
                    data.Transactions = [];
                }
                setApiResponse(prev => {
                    if (!prev) return data;

                    // Get existing transaction references for deduplication
                    const existingRefs = new Set(prev.Transactions.map(t => t.RecordReference));

                    // Filter out transactions that already exist
                    const newTransactions = data.Transactions.filter(
                        (transaction: Transaction) => !existingRefs.has(transaction.RecordReference)
                    );

                    return {
                        ...prev,
                        Transactions: [...prev.Transactions, ...newTransactions]
                    };
                });
            } else {
                // Set initial response
                setApiResponse(data);
            }

            // Check if there's more data (if we got less than 250 transactions, we've reached the end)
            setHasMoreData(data.Transactions && data.Transactions.length === 250);

        } catch (err) {
            setError(err instanceof Error ? err.message : 'An error occurred');
            if (isLoadMore) {
                isLoadingMoreRef.current = false;
            }
        } finally {
            if (isLoadMore) {
                setLoadingMore(false);
                isLoadingMoreRef.current = false;
            } else {
                setLoading(false);
            }
        }
    }, [row, selectedAreas, selectedOutlets, selectedBusinessDay, dashboardData, filterState, getParentTopic]);

    // Function to load more transactions
    const loadMoreTransactions = useCallback(() => {
        if (!isLoadingMoreRef.current && hasMoreData && !loadingMore) {
            isLoadingMoreRef.current = true;
            const nextPage = currentPage + 1;
            setCurrentPage(nextPage);
            callGetTransactionsAPI(nextPage, true);
        }
    }, [hasMoreData, loadingMore, currentPage, callGetTransactionsAPI]);

    // Call API when component mounts or dependencies change
    useEffect(() => {
        // Reset state when component mounts or dependencies change
        setCurrentPage(0);
        setHasMoreData(true);
        setApiResponse(null);
        isLoadingMoreRef.current = false;

        // Call API with initial page
        const loadInitialData = async () => {
            setLoading(true);
            setError(null);

            try {
                // Build filters based on clicked node hierarchy
                const filters = buildFiltersFromNodeHierarchy(row, dashboardData);
                setTransactionDetailsFilters({ filters: filters.filters, isTopic: filters.isTopic });
                const requestPayload = {
                    BusinessDay: selectedBusinessDay,
                    Topic: getParentTopic(row, dashboardData),
                    Filters: filters.filters,
                    Pagination: {
                        PageIndex: 0,
                        PageSize: 250 // Using 250 as per the curl example
                    }
                };

                const data = await apiHelper({
                    externalurl: '/api/v1/Reconciliation/GetReconciliationConsoleTransactions',
                    activityTag: 'GetReconciliationConsoleTransactions',
                    onlyBody: requestPayload,
                    method: 'POST',
                    headerContentType: 'application/json'
                });

                setApiResponse(data);
                setHasMoreData(data.Transactions && data.Transactions.length === 250); // Check against 250 now

            } catch (err) {
                setError(err instanceof Error ? err.message : 'An error occurred');
            } finally {
                setLoading(false);
            }
        };

        loadInitialData();
    }, [row, selectedTenant, selectedAreas, selectedOutlets, selectedBusinessDay, dashboardData, tenantsData, topicsData, filterState, getParentTopic, buildFiltersFromNodeHierarchy]);

    const formatDate = (dateString: string) => {
        const date = new Date(dateString);
        if (isNaN(date.getDate())) {
            const [day, month, year] = dateString.split('/');
            return `${day}-${month}-${year}`;
        }
        const day = date.getDate().toString().padStart(2, '0');
        const month = (date.getMonth() + 1).toString().padStart(2, '0');
        const year = date.getFullYear();
        return `${day}-${month}-${year}`;
    };
    return (
        <div className={`fixed inset-0 z-50 dark:bg-gray-700 bg-white overflow-auto transition-all duration-300 ease-in-out ${isVisible && !isClosing
            ? 'opacity-100 transform translate-y-0'
            : 'opacity-0 transform translate-y-4'
            }`}>
            <div className="sticky top-0 bg-white border-b border-gray-200 px-6 py-4 flex items-center justify-between dark:bg-gray-700 dark:border-gray-600">
                <div className="py-3">
                    <GradientText>Transaction Details</GradientText>
                </div>
                <button
                    onClick={handleClose}
                    className="text-gray-400 cursor-pointer hover:text-red-600 text-2xl font-bold transition-colors duration-200"
                >
                    <X className="w-7 h-7" />
                </button>
            </div>

            {/* Content */}
            <div className="p-6">
                <div className="max-w-8xl mx-auto">
                    {loading && (
                        <div className={`bg-blue-50 border dark:bg-gray-600 border-blue-200 rounded-lg p-8 mb-6 transition-all duration-500 ease-out ${isVisible && !isClosing
                            ? 'opacity-100 transform translate-y-0'
                            : 'opacity-0 transform translate-y-8'
                            }`} style={{ transitionDelay: isVisible ? '100ms' : '0ms' }}>
                            <div className="text-center">
                                <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
                            </div>
                        </div>
                    )}

                    {/* Error State */}
                    {error && (
                        <div className={`bg-red-50 border border-red-200 rounded-lg p-6 mb-6 transition-all duration-500 ease-out ${isVisible && !isClosing
                            ? 'opacity-100 transform translate-y-0'
                            : 'opacity-0 transform translate-y-8'
                            }`} style={{ transitionDelay: isVisible ? '100ms' : '0ms' }}>
                            <h2 className="text-lg font-semibold text-red-900 mb-2">Error Loading Transaction Details</h2>
                            <p className="text-red-800">{error}</p>
                        </div>
                    )}

                    {/* API Response */}
                    {apiResponse && !loading && !error && (
                        <div className={`flex flex-col dark:bg-gray-800 dark:border-gray-600 bg-white border border-gray-200 rounded-lg p-6 transition-all duration-500 ease-out ${isVisible && !isClosing
                            ? 'opacity-100 transform translate-y-0'
                            : 'opacity-0 transform translate-y-8'
                            }`} style={{ transitionDelay: isVisible ? '200ms' : '0ms' }}>

                            {/* Criteria Section */}
                            <div className="mb-6 p-4 dark:bg-gray-700 bg-gray-50 rounded-lg border flex items-center gap-5 justify-start">
                                <h3 className="text-sm font-medium dark:text-white text-slate-800">Filters</h3>
                                <div className=" flex flex-wrap gap-5">
                                    {/* Show Area filters */}
                                    <div className="flex items-center gap-2 text-sm">
                                        <span className="font-medium dark:text-white text-gray-600">Topic:</span>
                                        <span className="text-gray-900 dark:text-white dark:bg-gray-500 bg-white px-2 py-1 rounded border">
                                            {topicsData.Topics.find(t => t.Tag === getParentTopic(row, dashboardData))?.Label}
                                        </span>
                                    </div>

                                    {/* Show applied filters from filter state */}
                                    {transactionDetailsFilters.filters.reverse().map(({ Tag, Values }) => {
                                        return (
                                            <div key={Tag} className="flex items-center gap-2 text-sm">
                                                <span className="font-medium dark:text-white text-gray-600">{filtersData.Filters.find(f => f.Tag === Tag)?.Label}:</span>
                                                <span className="text-gray-900 dark:text-white dark:bg-gray-500 bg-white px-2 py-1 rounded border">
                                                    {getFilterDisplayText(Tag, Values)}
                                                </span>
                                            </div>
                                        );
                                    })}

                                    <div className="flex items-center gap-2 text-sm">
                                        <span className="font-medium dark:text-white text-gray-600">Business Day:</span>
                                        <span className="text-gray-900 bg-white dark:text-white dark:bg-gray-500 px-2 py-1 rounded border">
                                            {formatDate(selectedBusinessDay)}
                                        </span>
                                    </div>


                                </div>
                            </div>

                            {apiResponse?.Transactions?.length ? <>
                                <TransactionTable
                                    data={apiResponse}
                                    onLoadMore={loadMoreTransactions}
                                    isLoadingMore={loadingMore}
                                    hasMoreData={hasMoreData}
                                    handleClose={handleClose}
                                /></>
                                : <div className="flex flex-col items-center justify-center"><h2 className="text-lg !m-0 text-center dark:text-white font-semibold text-slate-700 mb-4 flex items-center gap-2">
                                    No Transactions Found
                                </h2>
                                </div>
                            }
                        </div>
                    )}


                </div>
            </div>
        </div>
    );
}
